import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import {
  EntityRefDisplay,
  EntityRefPicker,
} from "@/components/fields/entity-ref-picker";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import { ArtistNestedType } from "@/pages/artists/schemas/artist.nested.form";

/**
 * `person.artistId` — the music artist this film/stage person also is. The form value is the bare
 * id; the name and photo shown here are looked up once, for display only.
 *
 * Clearing writes `""`, not null: `UpdatePersonCommand` skips null members, and both BFFs treat
 * an empty `artistId` as "no linked artist" (`string.IsNullOrEmpty`).
 */
export function ArtistRefField({ id }: AutoFormFieldProps) {
  const { get, search } = useArtistRepository();
  const form = useFormContext();
  const artistId: string | null | undefined = useWatch({ name: id });
  const [display, setDisplay] = React.useState<EntityRefDisplay | null>(null);

  React.useEffect(() => {
    if (!artistId) {
      setDisplay(null);
      return;
    }
    if (display?.href === `/artists/edit/${artistId}`) return;
    let cancelled = false;
    setDisplay({ name: artistId, href: `/artists/edit/${artistId}` });
    get(artistId)
      .then((artist) => {
        if (cancelled) return;
        setDisplay({
          name: artist?.name ?? artistId,
          imageUrl: artist?.media?.photos?.[0]?.url,
          href: `/artists/edit/${artistId}`,
        });
      })
      .catch((error) => console.error("linked artist read failed", error));
    return () => {
      cancelled = true;
    };
  }, [artistId]);

  const pick = (artist: ArtistNestedType) => {
    if (!artist.id) return;
    setDisplay({
      name: artist.name,
      imageUrl: artist.photo?.url ?? artist.media?.photos?.[0]?.url,
      href: `/artists/edit/${artist.id}`,
    });
    form.setValue(id, artist.id, { shouldDirty: true });
  };

  return (
    <EntityRefPicker<ArtistNestedType>
      current={display}
      emptyLabel="No linked artist"
      onSearch={search}
      accessorFn={(artist) => ({ id: artist.id ?? "", name: artist.name })}
      onPick={pick}
      onClear={() => form.setValue(id, "", { shouldDirty: true })}
    />
  );
}
